'use client';

import {useQuery} from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import {useSearchParams} from 'next/navigation';
import {type FC, useCallback, useMemo} from 'react';
import type {FilterValues} from '@/components/atoms/FilterExperiences/FilterExperiences';
import {PaginationControl} from '@/shared/components/Pagination/PaginationControl';
import {experiencesListOptions} from '../api/experiences';
import {ExperienceCardSkeleton} from '../ExperienceCard';
import {NoResults} from './NoResults';

const ExperienceCardShellLazy = dynamic(
  () =>
    import('../ExperienceCard/ExperienceCardShell').then(mod => ({
      default: mod.ExperienceCardShell,
    })),
  {
    loading: () => <div className="bg-gray-800/20 h-[400px] animate-pulse" />,
  },
);

interface ExperiencesListProps {
  className?: string;
}

/**
 * Компонент для отображения списка опытов/впечатлений
 * @param {ExperiencesListProps} props - Пропсы компонента
 */

export const ExperiencesList: FC<ExperiencesListProps> = ({className = ''}) => {
  const query = useSearchParams();
  const celebrityId = useMemo(() => Number(query.get('celebrityId')) || undefined, [query]);
  const page = useMemo(() => Number(query.get('page') || 1) - 1, [query]);
  const setPage = useCallback((page: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', (page + 1).toString());
    window.history.pushState({}, '', url.toString());
  }, []);

  const searchParams = useSearchParams();

  const filters = useMemo<Partial<FilterValues>>(() => {
    return {
      celebrities: searchParams.get('celebrities')
        ? searchParams.get('celebrities')?.split(',').map(Number)
        : undefined,
      celebrityName: searchParams.get('celebrityName') ?? undefined,
      type: searchParams.get('type') as
        | 'EXCLUSIVE_EXPERIENCE'
        | 'DIGITAL_EXPERIENCE'
        | 'EXCLUSIVE_ITEM'
        | 'all'
        | undefined,
      price: searchParams.get('price')
        ? (searchParams.get('price')?.split(',').map(Number) as [number, number])
        : undefined,
      sortBy: searchParams.get('sortBy') as 'ee_current_price' | undefined,
      sortType: searchParams.get('sortType') as 'asc' | 'desc' | undefined,
      page: searchParams.get('page') ? Number(searchParams.get('page')) : undefined,
    };
  }, [searchParams]);

  const {
    data: {items: experiences, totalPagesCount} = {
      items: [],
      totalPagesCount: 0,
    },
    isLoading,
    isFetching,
  } = useQuery({
    ...experiencesListOptions({
      celebrityId: celebrityId ? [celebrityId] : filters.celebrities,
      celebrityName: filters.celebrityName,
      sortBy: filters.sortBy,
      sortOrder: filters.sortType,
      category: filters.type,
      limit: 18,
      page: page + 1,
      minCurrentPrice: filters.price ? Number(filters.price[0]) : undefined,
      maxCurrentPrice: filters.price ? Number(filters.price[1]) : undefined,
    }),
  });

  if (isLoading || isFetching) {
    return (
      <div className={`grid grid-cols-1 gap-3 md:grid-cols-2 2md:grid-cols-3 lg:grid-cols-4 lg:gap-3 ${className}`}>
        {[1, 2, 3, 4].map(i => (
          <ExperienceCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!experiences?.length) {
    return <NoResults className="min-h-[314px]" />;
  }

  return (
    <>
      <div className={`grid grid-cols-1 gap-3 md:grid-cols-2 2md:grid-cols-3 lg:grid-cols-4 lg:gap-3 ${className}`}>
        {experiences.map((experience, index) => (
          <ExperienceCardShellLazy
            key={experience.id}
            experience={experience}
            isLCP={index === 0}
            countdownTimer={true}
          />
        ))}
      </div>
      {totalPagesCount > 1 && (
        <div className="mt-6 md:mt-8">
          <PaginationControl
            page={page}
            totalPages={totalPagesCount}
            firstPage={() => setPage(0)}
            lastPage={() => setPage(totalPagesCount - 1)}
            prevPage={() => setPage(Math.max(0, page - 1))}
            nextPage={() => setPage(Math.min(totalPagesCount - 1, page + 1))}
            setPage={setPage}
            className="pb-0 pt-0 lg:pb-0 lg:pt-0"
          />
        </div>
      )}
    </>
  );
};
